import {HttpClient, HttpClientEnhancer} from '@raison/network-facade';

/**
 * When browsers start unloading a page, they dispose of all resources, including active connections.
 * There's a Fetch API option keepalive to tell browsers to keep connections going, but it doesn't work
 * for GET requests in most browsers, only Safari keeps GET requests alive.
 *
 * This exists to just hide all errors that happen when page unloads. The preventDefault() functionality
 * is very unlikely to be used in this project, but it should be accounted for anyway.
 *
 * The event listeners of 'beforeunload' can impact bfcache behavior, so they should not be listened to unless needed.
 */
export const withUnloadCancellationPrevention: HttpClientEnhancer =
    next =>
    async (url, init): Promise<HttpClient.SyncOutput> => {
        let pageIsUnloading = false;
        let resumeExecution: (() => void) | undefined;
        const listener = (ev: BeforeUnloadEvent) => {
            if (ev.defaultPrevented) return;
            pageIsUnloading = true;
            // even if defaultPrevented is false
            // preventDefault can be called in listeners registered after this one
            setTimeout(() => {
                if (ev.defaultPrevented) {
                    pageIsUnloading = false;
                    resumeExecution?.();
                }
            });
        };
        window.addEventListener('beforeunload', listener);
        try {
            return await next(url, init);
        } catch (error) {
            if (pageIsUnloading) {
                return new Promise((_resolve, reject) => {
                    resumeExecution = () => reject(error);
                });
            }
            throw error;
        } finally {
            window.removeEventListener('beforeunload', listener);
        }
    };
